import { FC, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import DefaultImg from "../../../assets/img/default-image.svg";
import { useMissingDocs } from "../../../hooks/useMissingDocs";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { CandidateStorage } from "../../../services/CandidateStorage";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getCandidateData } from "../../../store/superadmin/thunks";
import { ICompletionStatus } from "../../../store/superadmin/types";
import { getLatestStage2Action } from "../../../utils/stage2ActionHelper";
import { capitalizeName } from "../../../utils/stringUtils";
import CustomButton from "../../UI/Buttons/CustomButton";
import LeftArrow from "../../UI/Icons/LeftArrow";

const MyProfile: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const missingDocs = useMissingDocs();
  const candidateDetails = useSelector(
    (state) => state.superadmin.reviewCandidateData
  );
  const [activeStage, setActiveStage] = useState<string | null>(
    sessionStorage.getItem("stage") || "stage1"
  );

  const referooStatus = candidateDetails?.referoo_status;

  const stepCompletion: ICompletionStatus | null = candidateDetails?.completion_status || null;

  const isAdminCompleteReopen = candidateDetails?.reopened_stages_status?.find(
    (s) => s.stage_number === 2 && s.status === 'reopened'
  )?.sections?.includes('admin_complete');

  const isAdminCompleteFromProfile = candidateDetails?.profile?.profile_steps?.find(
    (step) => step.action === 'approve_candidate_bypass_compliance_check'
  )

  // const canWeAllowstage2Edit = (isAdminCompleteFromProfile || isAdminCompleteReopen) && referooStatus !== "complete";
  

  const hasWWCDoc = candidateDetails?.documents?.some((doc) => doc.document_type === "working_with_children_check");
  const hasPoliceCheck = candidateDetails?.documents?.some((doc) => doc.document_type === "police_check");
  const hasRighttoWork = candidateDetails?.documents?.some((doc) => doc.document_type === "right_to_work");
  const hasReferenceCheck = candidateDetails?.documents?.some((doc) => doc.document_type === "reference_check") && candidateDetails?.documents?.some((doc) => doc.document_type === "reference_check_1");
  
  const latestStage2Action = getLatestStage2Action(
    candidateDetails?.profile?.profile_steps || [],
    candidateDetails?.reopened_stages_status,
    candidateDetails?.referoo_candidate?.created_at
    );

  const canWeAllowstage2Edit = latestStage2Action === "admin-complete";
  useEffect(() => {
    if (activeStage === "stage1") {
      navigate("/admin/candidate-profile/personal-details");
    } else if (activeStage === "stage2") {
      if(latestStage2Action === "referoo") {
        navigate("/admin/candidate-profile/personal-details");
      } else {
        navigate("/admin/candidate-profile/complaince-check/edit");
      }
      
    } else if (activeStage === "stage3") {
      navigate("/admin/candidate-profile/casual-and-fair-work-statement");
    }

    sessionStorage.setItem(
      "stage",
      activeStage !== null ? activeStage : "stage1"
    );
  }, [activeStage]);

  const handleStageClick = (stageId: string) => {
    setActiveStage(activeStage === stageId ? "stage1" : stageId);
  };

  useEffect(() => {
    const fetchCandidateDetails = async () => {
      // candidate id from sessionStorage
      dispatch(showTransparentPreloaderAction());
      const storedId = CandidateStorage.get();
      if (storedId !== null) {
        await dispatch(
          getCandidateData({ candidate_id: candidateDetails?.uuid })
        );
      }
      dispatch(hideTransparentPreloaderAction());
    };
    fetchCandidateDetails();
    document.body.classList.remove("modal-open");
  }, []);

  const handleBackToProfile = () => {
    navigate(AUTH_ROUTE.ADMIN_CANDIDATES);
  };


  return (
    
      <div className="d-flex flex-column gap-25 max-w-960px">
        <div className="d-flex flex-column flex-lg-row gap-50">
          <div className="onbaording-steps min-w-205px d-flex flex-column gap-20">
            <div className="d-flex flex-column gap-20">
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-off-white text-black"
                onClick={handleBackToProfile}
              >
                <LeftArrow />
                Back to Profile
              </CustomButton>
              <div className="d-flex gap-15 align-items-center">
                <div className="w-44px h-44px border-solid border-2 border-grey-dark radius-40px">
                  <img
                    alt="profile-picture"
                    width="40"
                    height="40"
                    className="rounded-circle object-fit-cover"
                    src={candidateDetails?.photo || DefaultImg}
                  />
                </div>
                <div className="d-none d-lg-flex flex-column gap-4 max-w-150px">
                  <div
                    className="fs-22 fw-400 text-black lh-1 text-truncate"
                    title={`${capitalizeName(candidateDetails?.first_name ?? "")} ${capitalizeName(candidateDetails?.last_name ?? "")}`}
                  >
                    {capitalizeName(candidateDetails?.first_name ?? "")} {capitalizeName(candidateDetails?.last_name ?? "")}
                  </div>
                </div>
              </div>
              <div
                className="onboarding-stage cursor-pointer d-flex flex-column gap-8"
                onClick={() => handleStageClick("stage1")}
              >
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 1
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="fs-16 fw-700 text-black lh-1 text-decoration-underline">
                    Personal Details &amp; Interview
                  </div>
                </div>
                <div className="fs-16 fw-400 text-black d-md-none">
                  Verify ID, upload qualifications, add your availabilities,
                  record a video interview
                </div>
              </div>
              {activeStage === "stage1" && (
                <div className="progress-container flex-column top-0">
                  <div className="d-flex flex-column">
                    <Link
                      className={`progress-step disabled_step ${
                        stepCompletion?.id_verification === 1 ? "completed" : ""
                      } ${
                        location.pathname ===
                        "/admin/candidate-profile/id-verification"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="1"
                      to="id-verification"
                      data-discover="true"
                    >
                      ID Verification
                    </Link>
                    <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                  </div>
                  <Link
                    className={`progress-step ${
                      stepCompletion?.personal_details === 1 ? "completed" : ""
                    } ${
                      location.pathname ===
                      "/admin/candidate-profile/personal-details"
                        ? "fw-700"
                        : ""
                    }`}
                    data-step="2"
                    to="personal-details"
                    data-discover="true"
                  >
                    Personal Details
                  </Link>
                  <Link
                    className={`progress-step ${
                      stepCompletion?.qualification === 1 && missingDocs?.length === 0 ? "completed" : ""
                    } ${
                      location.pathname ===
                      "/admin/candidate-profile/qualifications"
                        ? "fw-700"
                        : ""
                    }`}
                    data-step="3"
                    to="qualifications"
                    data-discover="true"
                  >
                    Qualifications
                  </Link>
                  <div className="d-flex flex-column">
                    <Link
                      className={`progress-step ${
                        stepCompletion?.jobpreference === 1 ? "completed" : ""
                      } ${
                        location.pathname ===
                        "/admin/candidate-profile/job-preferences"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="4"
                      to="job-preferences"
                      data-discover="true"
                    >
                      Job Preferences
                    </Link>
                  </div>
                  <div className="d-flex flex-column">
                    <Link
                      className={`progress-step disabled_step completed ${
                        stepCompletion?.online_interview === 1 ? "completed" : ""
                      } ${
                        location.pathname ===
                        "/admin/candidate-profile/online-interview"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="5"
                      to="online-interview"
                      data-discover="true"
                    >
                      Online Interview
                    </Link>
                    <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                  </div>
                  
                </div>
              )}
            </div>
            <div className="d-flex flex-column gap-20">
              <div
                className="onboarding-stage cursor-pointer d-flex flex-column gap-8"
                onClick={() => canWeAllowstage2Edit ? handleStageClick("stage2") : null}
              >
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 2
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className={`fs-16 fw-700 text-black lh-1 ${latestStage2Action !== "referoo" ? "text-decoration-underline" : ""}`}>
                    Compliance Check
                  </div>
                  {latestStage2Action === "referoo" && (
                    <span className="fs-11 fw-400 text-grey-medium">Completed via Referoo - Editing Disabled</span>
                  )}
                  {
                    latestStage2Action === "admin-complete" ? (
                      <div className="fs-13 text-grey-medium">Completed by Super Admin</div>
                    ) : null
                  }
                </div>
              </div>
              {activeStage === "stage2" && referooStatus !== "complete" && (
                <div className="progress-container flex-column top-0">
                  <Link
                    className={`progress-step ${hasPoliceCheck ? "completed" : ""}`}
                    data-step="1"
                    to="#"
                    data-discover="true"
                  >
                    Police Check
                  </Link>
                  <Link
                    className={`progress-step ${hasWWCDoc ? "completed" : ""}`}
                    data-step="2"
                    to="#"
                    data-discover="true"
                  >
                    Working With Children
                  </Link>
                  <Link
                    className={`progress-step ${hasRighttoWork ? "completed" : ""}`}
                    data-step="3"
                    to="#"
                    data-discover="true"
                  >
                    Right To Work
                  </Link>
                  <Link
                    className={`progress-step ${hasReferenceCheck ? "completed" : ""}`}
                    data-step="4"
                    to="#"
                    data-discover="true"
                  >
                    Reference Check
                  </Link>
                </div>
              )}
            </div>
            <div className="d-flex flex-column gap-20">
              <div
                className="onboarding-stage cursor-pointer d-flex flex-column gap-8"
                onClick={() => handleStageClick("stage3")}
              >
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 3
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="fs-16 fw-700 text-black lh-1 text-decoration-underline">
                    Documents &amp; Contract
                  </div>
                </div>
              </div>
              {activeStage === "stage3" && (
                <div className="progress-container flex-column top-0">

                 <div className="d-flex flex-column min-h-40px">
                    <Link
                      className={`progress-step disabled_step ${
                        stepCompletion?.tfn_form === 1 ? "completed" : ""
                      } ${
                        location.pathname ===
                        "/admin/candidate-profile/tfn-declaration"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="1"
                      to="tfn-declaration"
                      data-discover="true"
                    >
                      TFN Declaration
                    </Link>
                    <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                  </div>

                  <div className="d-flex flex-column min-h-40px">
                    <Link
                      className={`progress-step disabled_step ${
                        stepCompletion?.superannuation_form === 1
                          ? "completed"
                          : ""
                      } ${
                        location.pathname ===
                        "/admin/candidate-profile/superannuation"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="2"
                      to="superannuation"
                      data-discover="true"
                    >
                      Superannuation
                    </Link>
                    <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                  </div> 
                  <div className="d-flex flex-column min-h-40px">
                    <Link
                      className={`progress-step disabled_step ${
                        stepCompletion?.contract_form === 1 ? "completed" : ""
                      } ${
                        location.pathname ===
                        "/admin/candidate-profile/contract-form"
                        ? "fw-700"
                        : ""
                    }`}
                    data-step="1"
                    to="contract-form"
                    data-discover="true"
                  >
                    Contract
                  </Link>
                  <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                </div>
                <div className="d-flex flex-column min-h-40px">
                  <Link
                    className={`progress-step ${
                      stepCompletion?.casual_fair_work_statement === 1 ? "completed" : ""
                    } ${
                      location.pathname ===
                      "/admin/candidate-profile/casual-and-fair-work-statement"
                        ? "fw-700"
                        : ""
                    }`}
                    data-step="1"
                    to="casual-and-fair-work-statement"
                    data-discover="true"
                  >
                    Casual and Fair Work Statement
                  </Link>
                  {/* <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span> */}
                </div>
                   <Link
                    className={`progress-step ${
                      stepCompletion?.bankAccount === 1 ? "completed" : ""
                    } ${
                      location.pathname ===
                      "/admin/candidate-profile/bank-account"
                        ? "fw-700"
                        : ""
                    }`}
                    data-step="2"
                    to="bank-account"
                    data-discover="true"
                  >
                    Bank Account
                  </Link>

                  <Link
                    className={`progress-step ${
                      stepCompletion?.emergency_info === 1 ? "completed" : ""
                    } ${
                      location.pathname ===
                      "/admin/candidate-profile/next-of-kin"
                        ? "fw-700"
                        : ""
                    }`}
                    data-step="3"
                    to="next-of-kin"
                    data-discover="true"
                  >
                    Next of Kin
                  </Link>

                </div>
              )}
            </div>
          </div>
          <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
            <Outlet />
          </div>
        </div>
      </div>
    
  );
};

export default MyProfile;
